import React, { useState, useEffect, useContext } from 'react';
import { UserCheck, AlertTriangle, X, RefreshCw } from 'lucide-react';
import UserAutocomplete, { UserOption } from './UserAutocomplete';
import { impersonationService } from '../../services/impersonationService';
import { UserContext } from '../../contexts/UserContext';
import { useOrganizations } from '../../contexts/OrganizationsContext';
import axios from '../../axios-config';

interface ImpersonationPanelProps {
  onClose?: () => void;
}

const ImpersonationPanel: React.FC<ImpersonationPanelProps> = ({ onClose }) => {
  const { userProfile, fetchUserProfile, isImpersonating, setImpersonationState } = useContext(UserContext);
  const { organizations } = useOrganizations();
  
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Function to manually refresh users
  const refreshUsers = async () => {
    console.log('refreshUsers called');
    console.log('userProfile:', userProfile);
    console.log('organizations:', organizations);
    
    if (!userProfile?.mainRole?.toLowerCase().includes('admin')) {
      console.log('User is not admin, skipping user load. mainRole:', userProfile?.mainRole);
      setError('You must be an admin to use this feature.');
      return;
    }
    
    setIsLoadingUsers(true);
    setError('');
    console.log('Loading users from organizations:', organizations);
    
    try {
      const allUsers: UserOption[] = [];
      
      // Try different endpoints to find users
      try {
        console.log('Trying general /api/users endpoint...');
        const response = await axios.get('/api/users', {
          withCredentials: true
        });
        
        console.log('Response from /api/users:', response);
        console.log('Response data:', response.data);
        
        if (response.data && Array.isArray(response.data)) {
          console.log('Processing array of users:', response.data.length);
          const generalUsers = response.data.map((user: any) => {
            console.log('Processing user:', user);
            return {
              userId: user.id || user.userId,
              fullName: user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
              email: user.email,
              role: user.role || user.mainRole || 'User',
              avatar: user.avatar || user.picture
            };
          });
          console.log('Mapped users from /api/users:', generalUsers);
          allUsers.push(...generalUsers);
        } else {
          console.log('No valid array data from /api/users');
        }
      } catch (err: any) {
        console.warn('General /api/users endpoint failed:', err);
        console.warn('Error status:', err.response?.status);
        console.warn('Error data:', err.response?.data);
      }

      // Try additional endpoints if the main one fails or returns no data
      if (allUsers.length === 0) {
        const alternativeEndpoints = [
          '/api/AcornUsers',
          '/api/Users',
          '/api/profile/all',
          '/api/admin/users'
        ];

        for (const endpoint of alternativeEndpoints) {
          try {
            console.log(`Trying alternative endpoint: ${endpoint}`);
            const response = await axios.get(endpoint, {
              withCredentials: true
            });
            
            console.log(`Response from ${endpoint}:`, response.data);
            
            if (response.data && Array.isArray(response.data)) {
              const users = response.data.map((user: any) => ({
                userId: user.id || user.userId || user.acornUserId,
                fullName: user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                email: user.email,
                role: user.role || user.mainRole || 'User',
                avatar: user.avatar || user.picture
              }));
              console.log(`Mapped users from ${endpoint}:`, users);
              allUsers.push(...users);
              break; // Stop trying other endpoints if we found data
            }
          } catch (err: any) {
            console.warn(`Alternative endpoint ${endpoint} failed:`, err.response?.status);
          }
        }
      }

      // Fallback: Try organization endpoints if available
      if (allUsers.length === 0 && organizations && organizations.length > 0) {
        console.log('Trying organization endpoints...');
        for (const org of organizations) {
          console.log(`Fetching users from organization: ${org.organizationName} (${org.organizationId})`);
          try {
            const response = await axios.get(`/api/organizations/${org.organizationId}/users`, {
              withCredentials: true
            });
            
            console.log(`Response from ${org.organizationName}:`, response.data);
            
            if (response.data && Array.isArray(response.data)) {
              const orgUsers = response.data.map((user: any) => ({
                userId: user.id || user.userId,
                fullName: user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                email: user.email,
                role: user.role || user.mainRole || 'User',
                avatar: user.avatar || user.picture
              }));
              console.log(`Mapped users from ${org.organizationName}:`, orgUsers);
              allUsers.push(...orgUsers);
            }
          } catch (err: any) {
            console.warn(`Failed to load users from organization ${org.organizationName}:`, err);
            console.warn('Org error status:', err.response?.status);
            console.warn('Org error data:', err.response?.data);
          }
        }
      } else if (allUsers.length === 0) {
        console.log('No organizations available for fallback and no users found from main endpoints');
      }
      
      console.log('All users before filtering:', allUsers);
      
      // Filter out current admin and duplicates
      const uniqueUsers = allUsers.filter((user, index, self) => {
        const isNotCurrentUser = user.userId !== userProfile?.userId;
        const hasEmail = !!user.email;
        const isUnique = index === self.findIndex(u => u.userId === user.userId);
        
        console.log(`User ${user.fullName}: isNotCurrentUser=${isNotCurrentUser}, hasEmail=${hasEmail}, isUnique=${isUnique}`);
        
        return isNotCurrentUser && hasEmail && isUnique;
      });
      
      console.log(`Found ${uniqueUsers.length} users for impersonation:`, uniqueUsers);
      setAvailableUsers(uniqueUsers);
      
      if (uniqueUsers.length === 0) {
        setError('No users found. This could be due to insufficient permissions or no users in the system.');
      } else {
        setError(''); // Clear any previous errors
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Load users on mount
  useEffect(() => {
    if (userProfile && organizations) {
      refreshUsers();
    }
  }, [userProfile, organizations]);

  const handleImpersonate = async () => {
    if (!selectedUserId) {
      setError('Please select a user to impersonate.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await impersonationService.impersonateUser(selectedUserId);
      
      if (response.success) {
        setSuccess(response.message);
        setImpersonationState(true, response.impersonatedUser);
        
        setTimeout(async () => {
          await fetchUserProfile();
          window.location.reload();
        }, 1000);
      } else {
        setError('Failed to impersonate user. Please try again.');
      }
    } catch (err: any) {
      console.error('Impersonation error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setError('You do not have permission to impersonate users.');
      } else {
        setError('Failed to impersonate user. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      await impersonationService.stopImpersonation();
      setImpersonationState(false);
      setSuccess('Stopped impersonation successfully.');
      
      setTimeout(async () => {
        await fetchUserProfile();
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      console.error('Stop impersonation error:', err);
      setError('Failed to stop impersonation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-indigo-50 rounded-xl shadow-md border border-indigo-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <UserCheck className="w-6 h-6 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-800">User Impersonation</h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Warning Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
          <p className="text-sm text-yellow-700 mt-1">
            Impersonation allows you to act as another user. Use this feature responsibly and only for legitimate administrative purposes.
            All actions performed while impersonating will be logged.
          </p>
        </div>
      </div>

      {/* Current Impersonation Status */}
      {isImpersonating && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-red-800">
                Currently impersonating a user
              </span>
            </div>
            <button
              onClick={handleStopImpersonation}
              disabled={isLoading}
              className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Stop Impersonation'}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Debug Information */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Debug Information</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <div>User Profile: {userProfile ? 'Loaded' : 'Not loaded'}</div>
            <div>Main Role: {userProfile?.mainRole || 'None'}</div>
            <div>Organizations: {organizations?.length || 0}</div>
            <div>Available Users: {availableUsers.length}</div>
            <div>Is Loading Users: {isLoadingUsers ? 'Yes' : 'No'}</div>
            {availableUsers.length > 0 && (
              <div>
                <div>Sample Users:</div>
                <ul className="list-disc list-inside ml-2">
                  {availableUsers.slice(0, 3).map(user => (
                    <li key={user.userId}>{user.fullName}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Selection */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select User to Impersonate
            </label>
            <button
              onClick={refreshUsers}
              disabled={isLoadingUsers}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingUsers ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => {
                  // Add test users for debugging
                  const testUsers: UserOption[] = [
                    {
                      userId: 'test-1',
                      fullName: 'Test User 1',
                      email: 'test1@example.com',
                      role: 'Student'
                    },
                    {
                      userId: 'test-2',
                      fullName: 'Test User 2',
                      email: 'test2@example.com',
                      role: 'Teacher'
                    }
                  ];
                  setAvailableUsers(testUsers);
                  console.log('Added test users:', testUsers);
                }}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
              >
                Add Test Users
              </button>
            )}
          </div>
          {isLoadingUsers ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading users...
            </div>
          ) : (
            <UserAutocomplete
              users={availableUsers}
              onSelect={setSelectedUserId}
              selectedUserId={selectedUserId}
              placeholder="Search by name, email, or role..."
              disabled={isLoading || isImpersonating}
            />
          )}
        </div>

        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-gray-500">
            {availableUsers.length} users available
          </div>
          <button
            onClick={handleImpersonate}
            disabled={!selectedUserId || isLoading || isImpersonating || isLoadingUsers}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <UserCheck className="w-4 h-4" />
            )}
            Start Impersonation
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationPanel;
